import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { formatMoney } from "@/lib/formatters"
import { ReportList, ReportRow } from "@/features/staff/reports/report-block"
import { tReports, tSharedReports } from "@/features/staff/reports/format"
import type { CaptureReport, CapturePerson } from "@/features/staff/reports/types"

/**
 * R4 — "Capturado por", and the title is the whole point.
 *
 * This was asked for as "ventas por mesero". It cannot be that.
 * `location_orders.user_id` is `Current.user` at creation and two of the three
 * creation paths are the DINER — the QR dine-in controller and pickup
 * materialization. There is no `waiter_id` on the table. A list titled "por
 * mesero" would file diners among the waiters on any QR-driven restaurant and
 * credit the manager who rang one round on somebody else's table, which is worse
 * than shipping nothing.
 *
 * So it answers the question the column can actually answer, and the
 * self-service bucket is a first-class row rather than a footnote: on a
 * QR-driven floor it is most of the money, and saying so IS the report. It is
 * one row and never a list of names — the restaurant's customers do not belong
 * on a staff performance screen.
 *
 * True per-server attribution needs a column and a product decision. Its own
 * issue.
 */

/**
 * A former staff member keeps their rows. They captured those orders at a till,
 * and dropping them into the self-service bucket would show the owner a QR surge
 * that never happened — the same class of lie the rename above exists to
 * prevent. The row says they have left instead of pretending they never were.
 */
function personSublabel(person: CapturePerson) {
  const role = person.roleLabel ?? tReports("capture_role_unknown")

  return person.former ? `${role} · ${tSharedReports("notes.former")}` : role
}

export function ReportCaptureBlock({ capture }: { capture: CaptureReport }) {
  if (capture.orderCount === 0) return null

  return (
    <Card density="compact" data-testid="report-capture">
      <CardHeader className="border-b">
        <CardTitle>{tReports("capture_title")}</CardTitle>
        <CardDescription>{tSharedReports("notes.capture_basis")}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <ReportList testId="report-capture-people">
          {capture.people.map((person) => (
            <ReportRow
              key={`${person.name ?? "unknown"}-${person.role ?? "none"}`}
              testId="report-capture-person"
              tone={person.former ? "disabled" : "available"}
              label={person.name ?? tReports("actor_unknown")}
              sublabel={personSublabel(person)}
              meta={tReports("orders_count", { count: person.orderCount })}
              amount={formatMoney(person.amount)}
            />
          ))}

          {/* Always rendered when it carries anything, and never collapsed into
              the people above. A restaurant whose diners ring up their own food
              is reading the shape of its own service here. */}
          {capture.selfService.orderCount > 0 ? (
            <ReportRow
              testId="report-capture-self-service"
              tone="reserved"
              label={capture.selfService.label}
              sublabel={tReports("capture_self_service_meta", {
                count: capture.selfService.dinerCount,
              })}
              meta={tReports("orders_count", { count: capture.selfService.orderCount })}
              amount={formatMoney(capture.selfService.amount)}
            />
          ) : null}
        </ReportList>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="tabular-nums">
            {tReports("orders_count", { count: capture.orderCount })}
          </Badge>
          <Badge variant="outline" className="tabular-nums">
            {tReports("capture_items_count", { count: capture.itemCount })}
          </Badge>
        </div>
      </CardContent>
    </Card>
  )
}
